import { Component, ReactNode } from 'react';
import styled from 'styled-components';
import { FormItemLabel } from './FormItemLabel';
import { SvgIcon } from '../SvgIcons/svgIcons';
import { theme } from '../theme';

const FormItemContainerElement = styled.div<IFormItemProps>`
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
  margin: ${props => (props.spacingBelow ? props.theme.vars.newForm.item.margin : 0)};
  height: ${props => (props.fullscreen ? 'calc(100% - 30px)' : 'auto')};
  display: ${({ hasCharCount }) => (hasCharCount ? 'flex' : 'block')};
  flex-direction: ${({ isBottomCharCount }) => (isBottomCharCount ? 'column' : 'row')};
  align-items: ${({ isBottomCharCount }) => (isBottomCharCount ? 'end' : 'unset')};
  @media (max-width: 992px) {
    flex-direction: ${({ validationMessage, isBottomCharCount }) =>
      validationMessage || isBottomCharCount ? 'column' : 'row'};
  }
  :hover .input-state {
    ${props => !props.validationMessage && props.theme.partials.newForm.item.state.hover};
  }
  :focus-within .input-state {
    ${props => props.theme.partials.newForm.item.state.focus};
  }
  &.complete {
    .input-state {
      ${props => props.theme.partials.newForm.item.state.complete};
    }
  }
  &.error {
    .input-state {
      ${props => props.theme.partials.newForm.item.state.error};
    }
  }
  &.warning {
    .input-state {
      border-bottom: 2px solid ${props => props.theme.designToken.warning500};
    }
  }
`;

const FormItemElement = styled.div<IFormItemProps>`
  align-items: center;
  display: ${({ isMini }) => (isMini ? 'flex' : 'block')};
  height: ${props => (props.fullscreen ? '100%' : 'auto')};
  position: relative;
  width: ${props => (props.hasCharCount ? '100%' : 'auto')};
  min-width: ${props => (props.minWidth ? props.minWidth : 'auto')};
  max-width: ${props => (props.maxWidth ? props.maxWidth : 'auto')};
  &.disabled,
  &.disabled:hover {
    .input-state {
      border-bottom: ${props => `2px solid ${props.theme.vars.newColor.lightGS1}`};
    }
    :focus-within .input-state {
      box-shadow: none;
    }
  }

  .input-state {
    ${props =>
      props.validationMessage
        ? `border-bottom: 2px solid ${props.theme.designToken.warning50}`
        : props.hasCharCount
        ? 'border-bottom:2px solid transparent'
        : props.theme.partials.newForm.item.state.default};
  }
`;

const ValidationMessageElement = styled.div<IFormItemProps>`
  display: flex;
  align-items: center;
  margin-top: ${props => (props.hasCharCount ? '0' : '8px')};
  padding-left: ${props => (props.hasCharCount ? '10px' : '0')};
  white-space: ${props => (props.hasCharCount ? 'nowrap' : 'pre-wrap')};
  @media (max-width: 768px) {
    flex-direction: ${props => (props.validationMessage ? 'column' : 'row')};
    margin-top: 10px;
  }
  margin-left: 4px;
  > div {
    align-items: center;
    display: flex;
  }

  .validation-message {
    color: ${props =>
      props.hasCharCount
        ? props.theme.designToken.warning50
        : props.validationState === 'warning'
        ? props.theme.designToken.warning700
        : props.theme.designToken.error500};
    font-weight: 400;
    margin: 0;
  }
  svg {
    color: ${props =>
      props.hasCharCount
        ? props.theme.designToken.warning50
        : props.validationState === 'warning'
        ? props.theme.designToken.warning700
        : props.theme.designToken.error500};
    path {
      fill: ${props =>
        props.hasCharCount
          ? props.theme.designToken.warning50
          : props.validationState === 'warning'
          ? props.theme.designToken.warning700
          : props.theme.designToken.error500};
    }
  }
`;

export const CharContainer = styled.div<IFormItemProps>`
  background: transparent;
  display: ${props => (props.hasCharCount && props.focusMode ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  width: 80px;
  color: ${props =>
    props.charLength > 0 && props.charLength <= 4
      ? props.theme.designToken.warning50
      : props.charLength === 0
      ? props.theme.designToken.neutral300
      : props.theme.designToken.success300};
  border: ${props =>
    props.charLength > 0 && props.charLength <= 4
      ? `1px solid ${props.theme.designToken.warning50}`
      : props.charLength === 0
      ? `1px solid ${props.theme.designToken.neutral300}`
      : `1px solid ${props.theme.designToken.success300}`};
`;

const BottomCharLength = styled.div<IFormItemProps>`
  margin-top: 5px;
`;

const NumberInputUnit = styled.div<IFormItemProps>`
  position: absolute;
  right: -33px;
  top: 52%;
`;

export type ValidationState = 'complete' | 'warning' | 'error' | null | '';

export interface IFormItemProps {
  /** Input label */
  label?: string;
  color?: string;
  /** Specifies which form element a label is bound to */
  for?: string;
  /** Input validation message */
  validationMessage?: string;
  /** Input validation state */
  validationState?: ValidationState;
  /** Adds spacing below the component (default: true) */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the form element is used for */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string | string[];
  /** Optional tooltip description list. Deafults to undefined */
  tooltipContent?: JSX.Element;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** Optional tooltip to display. This replaces the existing tooltip component. */
  tooltipComponent?: ReactNode;
  /** Whether to disable the FormItem or not.  Defaults to false. */
  disabled?: boolean;
  /** Allows the FormItem to span the full width of it's container. */
  fullWidth?: boolean;
  fullscreen?: boolean;
  isMini?: boolean;
  hasCharCount?: boolean;
  isLabelBold?: boolean;
  focusMode?: boolean;
  /** Show the password toggle icon */
  onPasswordToggleClick?: (e: React.MouseEvent) => void;
  /** Label value for the password toggle button. Default: 'Toggle password' */
  passwordToggleLabel?: string;
  /** Is the password currently visible */
  passwordVisible?: boolean;
  /** INPUT charachter length*/
  charLength?: number;
  /** INPUT container minWidth*/
  minWidth?: string;
  /** INPUT container maxWidth*/
  maxWidth?: string;
  isBottomCharCount?: boolean;
  type?: string;
  numberInputUnit?: string;
}

export class FormItem extends Component<IFormItemProps> {
  public static defaultProps: Pick<IFormItemProps, 'spacingBelow'> = {
    spacingBelow: true,
  };
  public render() {
    return (
      <FormItemContainerElement
        className={this.getValidationState(this.props.validationState)}
        spacingBelow={this.props.spacingBelow}
        fullscreen={this.props.fullscreen}
        hasCharCount={this.props.hasCharCount}
        validationMessage={this.props.validationMessage}
        isBottomCharCount={this.props.isBottomCharCount}
        fullWidth={this.props.fullWidth}
      >
        <FormItemElement
          className={this.props.disabled ? 'disabled' : null}
          fullscreen={this.props.fullscreen}
          isMini={this.props.isMini}
          hasCharCount={this.props.hasCharCount}
          minWidth={this.props.minWidth}
          maxWidth={this.props.maxWidth}
          validationMessage={this.props.validationMessage}
        >
          {this.props.label && (
            <FormItemLabel
              name={this.props.label}
              color={this.props.color}
              hint={this.props.hint}
              tooltipHeading={this.props.tooltipHeading}
              tooltipDescription={this.props.tooltipDescription}
              tooltipContent={this.props.tooltipContent}
              tooltipWidth={this.props.tooltipWidth}
              tooltipComponent={this.props.tooltipComponent}
              disabled={this.props.disabled}
              for={this.props.for}
              isMini={this.props.isMini}
              isBold={this.props.isLabelBold}
              onPasswordToggleClick={this.props.onPasswordToggleClick}
              passwordToggleLabel={this.props.passwordToggleLabel}
              passwordVisible={this.props.passwordVisible}
              type={this.props.type}
            />
          )}
          {this.props.children}
          {!this.props.isMini && <div className="input-state" />}
          {this.props.numberInputUnit && <NumberInputUnit>{this.props.numberInputUnit}</NumberInputUnit>}
        </FormItemElement>
        {this.props.hasCharCount && this.props.focusMode ? (
          <CharContainer
            hasCharCount={this.props.hasCharCount}
            focusMode={this.props.focusMode}
            charLength={this.props.charLength}
          >{`${this.props.charLength} / 100`}</CharContainer>
        ) : this.props.hasCharCount && this.props.isBottomCharCount ? (
          <BottomCharLength>{`${this.props.charLength} / 100`}</BottomCharLength>
        ) : (
          ''
        )}
        {this.props.validationMessage && (
          <ValidationMessageElement
            hasCharCount={this.props.hasCharCount}
            validationState={this.getValidationState(this.props.validationState)}
          >
            {this.props.validationState === 'warning' ? (
              <SvgIcon name="triangle-exclamation" color={theme.designToken.warning700} />
            ) : (
              <SvgIcon name="ban" color={theme.designToken.error500} />
            )}
            <p className="validation-message" aria-label="validation message">
              {this.props.validationMessage}
            </p>
          </ValidationMessageElement>
        )}
      </FormItemContainerElement>
    );
  }
  private getValidationState = state => {
    if (state === 'complete') {
      return state;
    } else if (state === 'warning') {
      return state;
    } else if (state === 'error') {
      return state;
    } else {
      return null;
    }
  };
}
